import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate } from "@/pages/admin/util";
import { Loader2, RefreshCw, Check, X, Eye, EyeOff, Star } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { k: "PENDING", label: "Pending" },
  { k: "APPROVED", label: "Approved" },
  { k: "REJECTED", label: "Rejected" },
];

export default function AdminReviews() {
  const [tab, setTab] = useState("PENDING");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/reviews?status=${tab}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load reviews");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  async function setStatus(id, status) {
    setBusyId(id);
    try {
      await adminApi.post(`/reviews/${id}/status`, { status });
      toast.success(`Marked ${status.toLowerCase()}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setBusyId(null);
    }
  }

  async function toggleDisplay(id, displayed) {
    setBusyId(id);
    try {
      await adminApi.post(`/reviews/${id}/display`, { displayed: !displayed });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle display");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-reviews-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">Customer reviews</p>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Reviews & Testimonials</h1>
          <p className="text-sm text-[#475569] mt-1">Approved reviews auto-replace the soft testimonials on the homepage when 3+ are approved.</p>
        </div>
        <button onClick={load} className="btn-outline h-10 py-0 px-4 text-sm" data-testid="reviews-refresh-btn">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="reviews-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm rounded-full transition ${
              tab === t.k
                ? "bg-[#064E2E] text-white"
                : "bg-white border border-[#D7E7DF] text-[#475569] hover:border-[#064E2E]"
            }`}
            data-testid={`reviews-tab-${t.k.toLowerCase()}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {loading && (
          <div className="card-default text-center text-[#475569]">
            <Loader2 className="animate-spin inline" size={18} />
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="card-default text-center py-10" data-testid="reviews-empty-state">
            <Star size={28} className="inline text-[#94A3B8] mb-3"/>
            <p className="text-[#071A33] font-medium">No {tab.toLowerCase()} reviews yet</p>
            <p className="text-xs text-[#475569] mt-1 max-w-md mx-auto">
              {tab === "PENDING"
                ? "Customer-submitted reviews land here for approval. Share your /leave-review link to invite reviews."
                : tab === "APPROVED"
                ? "Approved reviews appear on the public homepage when 3 or more are approved."
                : "Rejected reviews stay hidden but are kept on file for the audit trail."}
            </p>
          </div>
        )}
        {!loading &&
          items.map((r) => (
            <div key={r.id} className="card-default" data-testid={`review-card-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <p className="text-[#071A33] leading-relaxed text-[15px]">
                    <span className="font-display text-2xl text-[#B88A2A] leading-none align-top mr-1">&ldquo;</span>
                    {r.comment}
                  </p>
                  <p className="mt-3 text-sm text-[#475569]">
                    — <span className="font-medium">{r.first_name}</span>
                    {r.location ? <span className="text-[#94A3B8]"> · {r.location}</span> : null}
                    <span className="text-[#94A3B8] text-xs ml-2">{fmtDate(r.created_at, true)}</span>
                  </p>
                  {r.source_id && (
                    <p className="text-xs text-[#94A3B8] mt-1">Source: {r.source_id}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {r.status === "PENDING" && (
                    <>
                      <button
                        onClick={() => setStatus(r.id, "APPROVED")}
                        disabled={busyId === r.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#064E2E] hover:bg-[#0F766E] text-white px-4 py-2 text-sm font-medium transition"
                        data-testid={`approve-btn-${r.id}`}
                      >
                        <Check size={14} /> Approve
                      </button>
                      <button
                        onClick={() => setStatus(r.id, "REJECTED")}
                        disabled={busyId === r.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#FCA5A5] text-[#DC2626] hover:bg-[#DC2626]/5 px-4 py-2 text-sm font-medium transition"
                        data-testid={`reject-btn-${r.id}`}
                      >
                        <X size={14} /> Reject
                      </button>
                    </>
                  )}
                  {r.status === "APPROVED" && (
                    <>
                      <button
                        onClick={() => toggleDisplay(r.id, r.displayed)}
                        disabled={busyId === r.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#D7E7DF] text-[#071A33] hover:bg-[#F7F4ED] px-4 py-2 text-sm font-medium transition"
                        data-testid={`toggle-display-${r.id}`}
                      >
                        {r.displayed ? <Eye size={14} /> : <EyeOff size={14} className="text-[#94A3B8]" />}
                        {r.displayed ? "Showing" : "Hidden"}
                      </button>
                      <button
                        onClick={() => setStatus(r.id, "REJECTED")}
                        disabled={busyId === r.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#FCA5A5] text-[#DC2626] hover:bg-[#DC2626]/5 px-4 py-2 text-sm font-medium transition"
                      >
                        <X size={14} /> Unapprove
                      </button>
                    </>
                  )}
                  {r.status === "REJECTED" && (
                    <button
                      onClick={() => setStatus(r.id, "APPROVED")}
                      disabled={busyId === r.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#064E2E] hover:bg-[#0F766E] text-white px-4 py-2 text-sm font-medium transition"
                    >
                      <Check size={14} /> Restore
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
